import React, { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import styles from 'components/medicalHistory/page.module.scss';
import { actionTypeOfMedicalHistory } from 'reducer/actionType';
import {
  LOCAL_STORAGE_MH_KEY,
  StateTypeLocalStorage,
  usePersistMHReducer,
} from 'reducer/medicalHistory';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store/store';
import { NullablePatientDTO } from 'types/patients';
import { ReviewOfSystemOption } from 'components/medicalHistory/PageCommonIssues';
import {
  clearLocalStorage,
  isEmptyString,
  normalizePatientName,
  removeItemFromLocalStorage,
  parseErrorMessage,
  formatDateOnly,
} from 'utils/helper';
import Spinner from 'components/spinner/Spinner';
import {
  CommonSelectOption,
  OptionTypeOfSurgicalHistory,
  SurgicalHistoryLocalStorageType,
} from 'components/medicalHistory/types/types';
import {
  AddressDTO,
  FamilyHistoryDTO,
  MedicalHistoryDTO,
  PatientMedicalHistoryRequestDTO,
  PatientInsurancesRequestDTO,
  ReviewOfSystemElementDTO,
  SurgicalHistoryDTO,
  InsuranceDTO,
  ProviderContextDTO,
  ProviderEntryDTO,
} from 'artifacts/dtos';
import {
  InsuranceFormType,
  serializeInsuranceForm,
} from 'components/medicalHistory/insuranceId';
import { NullableAppointmentDetails } from 'types/types';
import Typography from '@mui/material/Typography';
import { appointmentIntakeStatusAction } from 'reduxStateSlice/appointment';
import MetaTag from 'components/meta';
import { appStateSettingsAction } from 'reduxStateSlice/appState';
import { ModuleTypes } from 'types/appState';
import axios from 'axios';
import { patientAction } from 'reduxStateSlice/patient';
import { organizationIds } from 'components/medicalHistory/helpers';
import isEmpty from 'lodash/isEmpty';
import dayjs from 'dayjs';
import isNil from 'lodash/isNil';
import {
  useAddPatientInsurance,
  useAddPatientMedicalHistory,
  useGetPatientByIdWithPharmacy,
} from 'hooks/useApi';
import useIncompleteTempoRedirect from 'hooks/useIncompleteTempoRedirect';
import useCommuniqueState from 'hooks/useCommuniqueState';

function mapPcpForSubmit(value: unknown): ProviderContextDTO | undefined {
  if (!value || typeof value !== 'object' || isEmpty(value)) {
    return undefined;
  }
  const v = value as Record<string, unknown>;

  if (v.providerEntry && typeof v.providerEntry === 'object' && !isEmpty(v.providerEntry)) {
    const ctx = value as ProviderContextDTO;
    return {
      ...ctx,
      lastSeenOn: ctx.lastSeenOn && dayjs(ctx.lastSeenOn as string).isValid()
        ? formatDateOnly(ctx.lastSeenOn as string)
        : null,
    };
  }

  const firstName = String(v.firstName ?? '');
  const lastName = String(v.lastName ?? '');
  const state = String(v.contactState ?? v.state ?? '');

  if (isEmptyString(firstName) && isEmptyString(lastName) && isEmptyString(state)) {
    return undefined;
  }

  const dateLastSeenRaw = v.dateLastSeen as string | undefined;
  const fullName = [firstName, lastName].filter((x) => !isEmptyString(x)).join(' ');

  const providerEntry: ProviderEntryDTO = {
    type: 'Individual',
    firstName: firstName || null,
    lastName: lastName || null,
    state: state || null,
    formattedNames: {
      fullName: fullName || null,
    },
  };

  return {
    providerEntry,
    firstSeenOn: v?.firstSeenOn as string | null | undefined,
    lastSeenOn: dayjs(dateLastSeenRaw).isValid() ? formatDateOnly(dateLastSeenRaw as string) : null,
  };
}

function getNewInsurances(insurances: InsuranceDTO[]) {
  return insurances?.filter((item) => isNil(item.insuranceId));
}

function PageMedicalHistoryComplete() {
  const { setCommuniqueData, communiqueData } = useCommuniqueState();
  const [state] = usePersistMHReducer();
  const reduxDispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const { mutateAsync: getPatientByIdWithPharmacy } = useGetPatientByIdWithPharmacy();
  const { mutateAsync: addPatientInsurance } = useAddPatientInsurance();
  const { mutateAsync: addPatientMedicalHistory } = useAddPatientMedicalHistory();

  useIncompleteTempoRedirect(
    !isLoading
    && errorMessage === ''
    && !communiqueData?.sendForms?.pharmacyAllergyMedicationsSubmitted
    && !communiqueData?.sendForms?.consentForm,
  );
  const appointmentData = useSelector(
    (state: RootState) => state.appointment,
  ) as NullableAppointmentDetails;

  const patient = useSelector(
    (state: RootState) => state.patient,
  ) as NullablePatientDTO;

  const hasInsurance = useMemo(
    () => !organizationIds.has(communiqueData?.organizationId!),
    [organizationIds, communiqueData],
  );

  const handleAddPatientInsurance = async (payload: PatientInsurancesRequestDTO) => {
    try {
      if ((payload.insurances ?? [])?.length > 0) {
        await addPatientInsurance(payload);
      }

      removeItemFromLocalStorage(LOCAL_STORAGE_MH_KEY);
      if (communiqueData?.sendForms?.pharmacyAllergyMedicationsSubmitted) {
        const fetchPatient = await getPatientByIdWithPharmacy(patient?.patientId ?? 0);
        reduxDispatch(patientAction(fetchPatient));
        reduxDispatch(
          appStateSettingsAction({
            name: ModuleTypes.pharmacyMedications,
            page: null,
          }),
        );
      } else if (communiqueData?.sendForms?.consentForm) {
        reduxDispatch(
          appStateSettingsAction({
            name: ModuleTypes.consentForm,
            page: null,
          }),
        );
      } else {
        clearLocalStorage();
      }
      setIsLoading(false);
    } catch (error: unknown) {
      if (axios.isAxiosError(error)) {
        setErrorMessage(`${parseErrorMessage(error)}`);
      } else {
        setErrorMessage(
          'Something went wrong on AddPatientInsuranceOwnership',
        );
      }
      setIsLoading(false);
    }
  };

  const handleAddMedicalHistory = async () => {
    const personalInformation = state.find(
      (e: StateTypeLocalStorage) => e.name === actionTypeOfMedicalHistory.PERSONAL_INFORMATION,
    );
    const primaryCarePhysician = state.find(
      (e: StateTypeLocalStorage) => e.name === actionTypeOfMedicalHistory.PRIMARY_CARE_PHYSICIAN,
    );
    const hasNoPrimaryCarePhysician = (
      primaryCarePhysician?.value as Record<string, unknown> | undefined
    )?.hasNoPrimaryCarePhysician === true;
    const surgicalHistory = state.find(
      (
        e: SurgicalHistoryLocalStorageType,
      ) => e.name === actionTypeOfMedicalHistory.SURGICAL_HISTORY,
    );

    const surgicalHistoryPayload: SurgicalHistoryDTO[] = [];
    surgicalHistory?.value?.forEach((item: OptionTypeOfSurgicalHistory) => {
      surgicalHistoryPayload.push({
        surgeryName: item.value,
        surgeryYear: isNil(item.year) ? null : formatDateOnly(item.year ?? ''),
      });
    });

    const medicalHistory = state.find(
      (e: StateTypeLocalStorage) => e.name === actionTypeOfMedicalHistory.MEDICAL_HISTORY,
    );

    const medicalHistoryPayload: MedicalHistoryDTO[] = medicalHistory?.value?.map(
      (item: CommonSelectOption) => ({
        conditionName: item.value ?? null,
      }),
    ) ?? [];

    const familyHistory = state.find(
      (e: StateTypeLocalStorage) => e.name === actionTypeOfMedicalHistory.FAMILY_HISTORY,
    );
    const familyHistoryPayload: FamilyHistoryDTO[] = familyHistory?.value;

    const socialHistory = state.find(
      (e: StateTypeLocalStorage) => e.name === actionTypeOfMedicalHistory.SOCIAL_HISTORY,
    );

    const reviewOfSystemElements = state.find(
      (e: StateTypeLocalStorage) => e.name === actionTypeOfMedicalHistory.ANY_RECENT_ISSUES_PAGES,
    );

    const reviewOfSystemElementsPayload:
      ReviewOfSystemElementDTO[] = reviewOfSystemElements?.value?.map(
        (item: ReviewOfSystemOption, index: number) => ({
          title: item.name,
          discriminator: item.discriminator,
        }),
      );

    const contactInformation = state.find(
      (e: StateTypeLocalStorage) => e.name === actionTypeOfMedicalHistory.CONTACT_INFORMATION,
    );

    const personalInfoValue = personalInformation.value;
    const heightFeet = Number(personalInfoValue.heightFeet);
    const heightInches = Number(personalInfoValue.heightInches);
    const weight = Number(personalInfoValue.weight);
    const { occupation, occupationStartDate } = personalInfoValue;

    const heightAsInches = heightFeet * 12 + heightInches; // as inches

    const contactValue = contactInformation.value;

    const patientAddress: AddressDTO = {
      unit: !isEmptyString(contactValue.unit) ? contactValue.unit : null,
      street: contactValue.street ?? null,
      city: contactValue.city ?? null,
      state: contactValue.contactState ?? null,
      zipCode: contactValue.zip ?? null,
      country: 'USA',
    };

    const { data: insuranceData, isCashPay } = state.find(
      (e: StateTypeLocalStorage) => e.name === actionTypeOfMedicalHistory.INSURANCE_ID,
    )?.value ?? {};

    const insurances = insuranceData?.map((insurance: InsuranceFormType) => (
      serializeInsuranceForm(
        insurance,
        patient,
        personalInfoValue.dob,
        patientAddress,
      )
    ));

    const addPatientInsurancePayload: PatientInsurancesRequestDTO = {
      patientId: communiqueData?.patientId ?? 0,
      insurances: getNewInsurances(insurances),
    };

    const isOutOfPocket = hasInsurance
      ? isCashPay : appointmentData?.appointment?.isOutOfPocket;

    const payload: PatientMedicalHistoryRequestDTO = {
      appointmentId: communiqueData?.appointmentId,
      isAppointmentOutOfPocket: isOutOfPocket,
      patientDTO: {
        patientId: patient?.patientId ?? 0,
        firstName: normalizePatientName(personalInfoValue.firstName),
        middleName: isEmptyString(personalInfoValue.middleName)
          ? null : normalizePatientName(personalInfoValue.middleName),
        lastName: normalizePatientName(personalInfoValue.lastName),
        dateOfBirth: formatDateOnly(personalInfoValue.dob),
        email: personalInfoValue.email ?? null,
        genderDescription: personalInfoValue.sex,
        shoeSize: personalInfoValue.shoeSize,
        weight: weight !== 0 ? weight : null,
        height: heightAsInches !== 0 ? heightAsInches : null,
        primaryCarePhysician: mapPcpForSubmit(primaryCarePhysician?.value),
        hasNoPrimaryCarePhysician,
        dosespotPharmacy: undefined,
        occupation: occupation !== '' ? occupation : null,
        occupationStartDate: isEmptyString(occupationStartDate)
          ? null : formatDateOnly(occupationStartDate),
        address: patientAddress as AddressDTO,
        medications: null,
        drugAllergies: null,
        nonDrugAllergies: null,
        surgicalHistories: surgicalHistoryPayload,
        medicalHistories: medicalHistoryPayload,
        socialHistory: isEmpty(socialHistory?.value) ? null : socialHistory?.value,
        familyHistories: familyHistoryPayload,
        reviewOfSystem: {
          reviewOfSystemElements: reviewOfSystemElementsPayload,
        },
      },
    };

    try {
      await addPatientMedicalHistory(payload);
      await handleAddPatientInsurance(addPatientInsurancePayload);
      if (!isNil(communiqueData)) {
        setCommuniqueData({
          ...communiqueData,
          sendForms: {
            ...communiqueData?.sendForms,
            medicalHistory: false,
          },
        });
      }
      reduxDispatch(appointmentIntakeStatusAction({ medicalHistorySubmitted: true }));
    } catch (error: unknown) {
      if (axios.isAxiosError(error)) {
        setErrorMessage(`${parseErrorMessage(error)}`);
      } else {
        setErrorMessage('Something went wrong on AddPatientMedicalHistory');
        console.error(
          '🚀 ~ error on Submitting AddPatientMedicalHistory : ',
          error,
        );
      }
      setIsLoading(false);
    }
  };

  useEffect(() => {
    handleAddMedicalHistory();
  }, []);

  return (
    <>
      <MetaTag
        title={
          // eslint-disable-next-line no-nested-ternary
          isLoading
            ? 'Submitting Medical History...'
            : errorMessage === ''
              ? 'The submission has been successful.'
              : errorMessage
        }
      />
      <Box pb={1}>
        <Box
          minHeight="70vh"
          mt={2}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {!isLoading && errorMessage === '' ? (
            <>
              <Typography fontSize="40px">Congratulations!</Typography>
              <p className={styles.titleTextSubText}>
                The submission has been successful.
              </p>
            </>
          ) : (
            isLoading && <Spinner />
          )}
          {errorMessage && (
            <p className="error-message">{errorMessage}</p>
          )}
        </Box>
      </Box>
    </>
  );
}

export default PageMedicalHistoryComplete;
